import React, { useEffect, useState } from "react";
import { Send, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/common";
import { DeliveryStatus } from "@/components/DeliveryStatus";
import { Link } from "react-router-dom";

export default function TestConsole() {
  const [domains, setDomains] = useState([]);
  const [form, setForm] = useState({ local: "noreply", domain: "", to: "", subject: "", html: "<h1>Hello 👋</h1>\n<p>This is a test email from RelayMX.</p>" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/domains").then((r) => {
      const verified = r.data.filter((d) => d.sending_ready);
      setDomains(verified);
      if (verified[0]) setForm((f) => ({ ...f, domain: verified[0].domain }));
    }).catch(() => {});
  }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        from_addr: `${form.local}@${form.domain}`,
        to: form.to.split(",").map((s) => s.trim()).filter(Boolean),
        subject: form.subject,
        html: form.html,
      };
      const { data } = await api.post("/send", payload);
      setResult(data);
      if (data.status === "accepted") toast.success("Accepted by recipient server");
      else if (data.status === "sandbox") toast.info("Stored in sandbox; not sent");
      else toast.error(data.status === "partial" ? "Some recipients were rejected" : "Delivery was not confirmed");
    } catch (err) { toast.error(apiErr(err)); setResult({ status: "failed", smtp_response: apiErr(err) }); } finally { setBusy(false); }
  };

  const ok = result && result.status === "accepted";

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">Test Console</h1>
        <p className="mt-1 text-sm text-slate-400">Compose and send a message through your relay.</p>
      </div>

      <DeliveryStatus />
      {domains.length === 0 && <div data-testid="send-no-verified-domain" className="text-sm text-amber-300">No DNS-verified sending domain. <Link data-testid="send-verify-domain-link" className="underline" to="/app/domains">Open Domains & DNS</Link></div>}
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={send} className="space-y-4 rounded-xl border border-slate-800 bg-slate-900/40 p-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">From</label>
            <div className="flex items-center gap-2">
              <input data-testid="email-send-from-local" value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} className="w-1/2 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60" />
              <span className="text-slate-500">@</span>
              <select data-testid="email-send-domain" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60">
                {domains.length === 0 && <option value="">No verified domain</option>}
                {domains.map((d) => <option key={d.id} value={d.domain}>{d.domain}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">To <span className="text-slate-500">(comma separated)</span></label>
            <input data-testid="email-send-to-input" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="user@example.com" required className="w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">Subject</label>
            <input data-testid="email-send-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Test email" required className="w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm outline-none focus:border-emerald-500/60" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-300">HTML body</label>
            <textarea data-testid="email-send-body" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} rows={7} className="mono w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-xs outline-none focus:border-emerald-500/60" />
          </div>
          <button data-testid="email-send-submit-button" disabled={busy || !form.domain} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 font-semibold text-[#04120c] hover:bg-emerald-400 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
          </button>
        </form>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-5">
            <div className="mb-3 mono text-xs uppercase tracking-wider text-slate-500">Response</div>
            {!result ? (
              <div className="py-10 text-center text-sm text-slate-600">Send a message to see the server response.</div>
            ) : (
              <div data-testid="send-result" className="space-y-3">
                <div className="flex items-center gap-2">
                  {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
                  <StatusPill status={result.status} testid="send-result-status" />
                </div>
                <div data-testid="send-smtp-response" className="mono break-all rounded-lg border border-slate-800 bg-[#0B0F17] p-3 text-xs text-slate-300">{result.smtp_response}</div>
                {result.message_id && <div className="mono text-xs text-slate-500">id: {result.message_id}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
